import type { LatLngExpression } from "leaflet";
import type { MapRef } from "react-map-gl/maplibre";
import {
  $cloud60,
  $occupiedStatus,
  $outOfServiceStatus,
  $unoccupiedStatus,
} from "../../cssjs/colors";
import { ISTATUS } from "../../models/common/CommonModels";
import { APIM_KEY, BadgeStatusType } from "../common/Constants";
import { formatBlockTimeByTimeZone } from "../common/DateHelperFunctions";
import { GateFlightInfo } from "./GateConstants";
import {
  AZURE_MAP_API_VERSION,
  BASE_MAP,
  IMapSas,
  mapTileType,
  MAP_RESOURCE_OCCUPANCY_STATUS,
  MAP_RESOURCE_STATUS,
  StandDisplayStatus,
  WEATHER_MAP,
} from "./MapConstants";

export const hasUsableDimensions = (element: HTMLElement) =>
  Number.isFinite(element.clientWidth) &&
  Number.isFinite(element.clientHeight) &&
  element.clientWidth > 0 &&
  element.clientHeight > 0;

export const hasValidCamera = (map: MapRef) => {
  const center = map.getCenter();
  return (
    Number.isFinite(center.lng) &&
    Number.isFinite(center.lat) &&
    Number.isFinite(map.getZoom()) &&
    Number.isFinite(map.getBearing()) &&
    Number.isFinite(map.getPitch())
  );
};

export const getAzureMapTileUrl = (
  airport: string,
  apiUrl: string,
  mapType: mapTileType = "BASE",
  sas?: IMapSas
) => {
  const tilesetId =
    mapType === "BASE" ? BASE_MAP.tileSetID : WEATHER_MAP.tileSetID;

  let url = apiUrl
    .replace("{airportCode}", airport)
    .replace("{apiVersion}", AZURE_MAP_API_VERSION)
    .replace("{tilesetId}", tilesetId)
    .replace("{apimKey}", APIM_KEY || "");

  if (sas) {
    // NOTE: `expires` must be sent exactly as returned by the SAS endpoint
    // (e.g. `2026-07-24T06:25:17.8836402Z`, colons un-encoded). The backend
    // validates `sig` against that literal string, so percent-encoding the
    // colons (`%3A`) changes the string it sees and the signature check fails
    // with a 500. Colons are valid, unreserved-enough characters in a query
    // string per RFC 3986, so passing them through as-is is safe.
    url = url.replace("{expires}", sas.expires).replace("{sig}", sas.sig);
  }

  return url;
};

/**
 * Builds the URL used to fetch the short-lived signature (`sig`/`expires`)
 * that authorizes subsequent map tile requests without a JWT per tile.
 */
export const getAzureMapSasUrl = (airport: string, apiUrl: string) => {
  return apiUrl.replace("{airportCode}", airport);
};

/**
 * Calculates the distance in meters between two geographic coordinates using the Haversine formula.
 * The Haversine formula determines the great-circle distance between two points on a sphere
 * given their longitudes and latitudes.
 */
export function haversineMeters(
  a: LatLngExpression,
  b: LatLngExpression
): number {
  const [lat1, lon1] = a as [number, number];
  const [lat2, lon2] = b as [number, number];

  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const sLat1 = Math.sin(dLat / 2);
  const sLon1 = Math.sin(dLon / 2);
  const aa =
    sLat1 * sLat1 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sLon1 * sLon1;
  return 2 * R * Math.asin(Math.sqrt(aa));
}

/**
 * Interpolates between two geographic coordinates (latitude/longitude) using linear interpolation.
 */
export function interpolateLatLng(
  start: LatLngExpression,
  end: LatLngExpression,
  t: number
): [number, number] {
  const [lat1, lon1] = start as [number, number];
  const [lat2, lon2] = end as [number, number];
  const lat = lat1 + (lat2 - lat1) * t;
  const lon = lon1 + (lon2 - lon1) * t;
  return [lat, lon];
}

/**
 * Compute three segments between start & end, with a small gap (meters) at each junction.
 * We shorten each segment near the joints by a `gapRatio` derived from meters/length.
 */
export function splitIntoThreeWithGaps(
  start: LatLngExpression,
  end: LatLngExpression,
  gapMeters = 6
): Array<[[number, number], [number, number]]> {
  const L = haversineMeters(start, end) || 1; // avoid div by zero
  const gapRatio = Math.min(0.02, Math.max(0, gapMeters / L)); // clamp to ≤ 2%

  // Raw split parameters
  const t0 = 0.0;
  const t1 = 1 / 3;
  const t2 = 2 / 3;
  const t3 = 1.0;

  const inset = gapRatio;

  // Segment 1: [t0, t1 - inset]
  const s1a = interpolateLatLng(start, end, t0);
  const s1b = interpolateLatLng(start, end, t1 - inset);

  // Segment 2: [t1 + inset, t2 - inset]
  const s2a = interpolateLatLng(start, end, t1 + inset);
  const s2b = interpolateLatLng(start, end, t2 - inset);

  // Segment 3: [t2 + inset, t3]
  const s3a = interpolateLatLng(start, end, t2 + inset);
  const s3b = interpolateLatLng(start, end, t3);

  return [
    [s1a, s1b],
    [s2a, s2b],
    [s3a, s3b],
  ];
}

export const getStandDisplayStatus = (
  status?: MAP_RESOURCE_STATUS,
  occupancy?: MAP_RESOURCE_OCCUPANCY_STATUS
): StandDisplayStatus => {
  const defaultStatus: StandDisplayStatus = {
    label: "UNKNOWN",
    type: "informative",
    color: $cloud60,
  };

  if (!status) {
    return defaultStatus;
  }

  if (status === "NOT OPERATIONAL") {
    return {
      label: "NOT OPERATIONAL",
      type: "severe",
      color: $outOfServiceStatus,
    };
  }

  if (occupancy === "OCCUPIED") {
    return { label: "OCCUPIED", type: "warning", color: $occupiedStatus };
  }
  if (occupancy === "UNOCCUPIED") {
    return { label: "AVAILABLE", type: "success", color: $unoccupiedStatus };
  }

  return defaultStatus;
};

/**
 * Returns a badge status type based on the given numeric value.
 */
export const getRSCBadgeStatusOnCode = (value: number): BadgeStatusType => {
  if (value >= 5) {
    return "success";
  }
  if (value >= 3) {
    return "warning";
  }
  return "danger";
};

/**
 * Returns a badge status type based on the given numeric value.
 */
export const getRSCBadgeStatus = (value: ISTATUS): BadgeStatusType => {
  if (value === "good") {
    return "success";
  }
  if (value === "moderate") {
    return "warning";
  }
  if (value === "poor") {
    return "danger";
  }
  return "informative";
};

/**
 * Maps raw flight data to a `GateFlightInfo` object, formatting block times for display.
 */
export const mapGateFlightInfo = (
  flightData: any,
  currentAirportTime: Date,
  airportTimeZone: string,
  timezone: string
): GateFlightInfo | any => {
  const arrTime = formatBlockTimeByTimeZone(
    timezone,
    airportTimeZone,
    flightData?.arrivalTime,
    currentAirportTime,
    flightData?.arrivalTimeType
  );

  const depTime = formatBlockTimeByTimeZone(
    timezone,
    airportTimeZone,
    flightData?.departureTime,
    currentAirportTime,
    flightData?.departureTimeType
  );

  const allocationStartTime = formatBlockTimeByTimeZone(
    timezone,
    airportTimeZone,
    flightData?.allocationStartTime,
    currentAirportTime,
    flightData?.allocationStartTimeType
  );

  const allocationEndTime = formatBlockTimeByTimeZone(
    timezone,
    airportTimeZone,
    flightData?.allocationEndTime,
    currentAirportTime,
    flightData?.allocationEndTimeType
  );

  return {
    allocationStartTime: allocationStartTime,
    allocationEndTime: allocationEndTime,

    arrivalTime: arrTime,
    arrivalTimeStatus: flightData?.arrivalTimeStatus,

    departureTime: depTime,
    departureTimeStatus: flightData?.departureTimeStatus,

    arrivalFlight: flightData?.arrivalFlight,
    departureFlight: flightData?.departureFlight,

    aircraftType: flightData?.aircraftType,
  };
};

/**
 * Converts a location tuple with string or number values to a tuple of numbers.
 * This is useful for ensuring that latitude and longitude values are in the correct numeric format.
 */
export const toLatLng = (
  location: [number | string, number | string]
): [number, number] => [Number(location[0]), Number(location[1])];

/**
 * Validates whether a given coordinate pair represents a valid geographic location.
 */
export const isValidCoordinate = (
  location: [number | string, number | string]
): boolean => {
  const [lat, lng] = location.map(Number);
  return (
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};
